import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, OnDestroy, OnInit } from '@angular/core';
import { NbAuthService, NbAuthToken } from '@nebular/auth';
import { IHttpHeaders, IHttpParameters } from 'projects/models/src/public-api';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, retry, take, takeUntil } from 'rxjs/operators';
import { HTTP_API_URL, NgHttpService } from '../abstracts';



@Injectable()
export class HttpService implements NgHttpService, OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private authStatus: boolean = true;

  constructor(
    @Inject(HTTP_API_URL)
    private url: string,
    private http: HttpClient,
    private authService: NbAuthService) { }

  ngOnInit(): void {
    this.authService.onAuthenticationChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe((authStatus: boolean) => this.authStatus = authStatus);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get<T>(endpoint: string, headers?: IHttpHeaders, parameters?: IHttpParameters): Observable<T> {
    if (this.authStatus) {
      const httpHeaders = this.createHttpHeaders(headers) as HttpHeaders;
      const httpParameters = this.createHttpParameters(parameters);
      const url = this.url + endpoint;
      return this.http.get(url, { headers: httpHeaders, params: httpParameters })
        .pipe(retry(3), catchError(this.handleError)) as Observable<T>;
    }
    else {
      return throwError('Not authenticated!');
    }
  }

  post<T>(endpoint: string, headers?: IHttpHeaders, parameters?: IHttpParameters, body?: any): Observable<T> {
    if (this.authStatus) {
      const httpHeaders = this.createHttpHeaders(headers) as HttpHeaders;
      const httpParameters = this.createHttpParameters(parameters);
      const url = this.url + endpoint;
      return this.http.post(url, body, { headers: httpHeaders, params: httpParameters })
        .pipe(retry(3), catchError(this.handleError)) as Observable<T>;
    }
    else {
      return throwError('Not authenticated!');
    }
  }

  put<T>(endpoint: string, headers?: IHttpHeaders, params?: IHttpParameters, body?: any): Observable<T> {
    if (this.authStatus) {
      const httpHeaders = this.createHttpHeaders(headers) as HttpHeaders;
      const url = this.url + endpoint;
      const httpParams = this.createHttpParameters(params);
      return this.http.put(url, body, { headers: httpHeaders, params: httpParams })
        .pipe(retry(3), catchError(this.handleError)) as Observable<T>;
    }
    else {
      return throwError('Not authenticated!');
    }
  }

  delete<T>(endpoint: string, headers?: IHttpHeaders, params?: IHttpParameters): Observable<T> {
    if (this.authStatus) {
      const httpHeaders = this.createHttpHeaders(headers) as HttpHeaders;
      const url = this.url + endpoint;
      const httpParams = this.createHttpParameters(params);
      return this.http.delete(url, { headers: httpHeaders, params: httpParams })
        .pipe(retry(3), catchError(this.handleError)) as Observable<T>;
    }
    else {
      return throwError('Not authenticated!');
    }
  }

  private createHttpHeaders(headers?: { [key: string]: any | any[] }): HttpHeaders {
    let httpHeaders = new HttpHeaders();
    if (headers) { Object.keys(headers).forEach(key => httpHeaders = httpHeaders.append(key, headers[key])); }
    return httpHeaders;
  }

  private createHttpParameters(parameters: IHttpParameters) {
    let httpParameters = new HttpParams();
    if (parameters) {
      Object.keys(parameters).forEach(key => httpParameters = httpParameters.append(key, parameters[key]));
    }
    return httpParameters;
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
      if (error.error instanceof ErrorEvent) console.error('An error occurred:', error.error.message);
      else console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
      return throwError('Something bad happened; please try again later.');
  }

}
